import React, { useState } from 'react';
import { HelpCircle, ChevronDown } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import SEO from '../components/SEO';

const FAQ = () => {
  const faqs = [
    {
      question: 'What services does NIVA Digital provide?',
      answer: 'Niva Digital helps growing businesses accelerate AI-Adoption, software delivery, optimize cloud infrastructure, and scale operations through AI-enabled delivery pods.'
    },
    {
      question: 'Where are your offices located?',
      answer: 'We have offices in two strategic locations: Westminster, Colorado (USA) and Bengaluru, India. This allows us to provide 24/7 support and round-the-clock productivity for our clients.'
    },
    {
      question: 'What is the Build-Operate-Transfer (BOT) model?',
      answer: 'Our BOT model involves three phases: Build - We establish your GCC with culture-first hiring. Operate - We manage day-to-day operations with robust governance. Transfer - We seamlessly hand over operations to your team with complete knowledge transfer.'
    },
    {
      question: 'What are PODs?',
      answer: 'PODs are pre-assembled, dedicated teams that work as cohesive, multi-disciplinary units. Each pod typically includes Sr AI Enabled Engineer, DevOps Engineers, CloudOps Specialists and infrastructure architects who operate as an extension of your in-house team.'
    },
    {
      question: 'How much can I save, without compromising quality?',
      answer: 'Our clients typically achieve 40-60% cost savings compared to hiring local resources, while maintaining enterprise-grade quality and expertise. We also help reduce cloud operational costs by 30-50% through optimization.'
    },
    {
      question: 'Are your services HIPAA compliant?',
      answer: 'Yes, we have extensive experience building HIPAA-compliant cloud infrastructure, particularly for healthcare and senior living care providers. We ensure all necessary security measures and compliance standards are met.'
    },
    {
      question: 'What is CloudOps RaaS?',
      answer: 'CloudOps Reliability as a Service (RaaS) goes beyond typical managed services by embedding reliability engineering as an always-on service layer. It includes proactive monitoring, SLA-backed uptime guarantees (99.9%+), automated incident response, and continuous improvement.'
    },
    {
      question: 'How quickly can you set up a POD?',
      answer: 'Depending on your requirements, we can typically establish operational readiness within weeks. Our pre-assembled POD model and established recruitment processes allow for rapid POD deployment.'
    },
    {
      question: 'What technologies and tools do you work with?',
      answer: 'Our teams are proficient in all modern technologies and AI tools.'
    },
    {
      question: 'Do you offer partnership opportunities?',
      answer: 'Yes! We offer Technology Partner, Referral Partner, and Reseller Partner programs. Each partnership type comes with its own benefits including co-marketing opportunities, technical support, and revenue sharing. Visit our Partnerships page to learn more.'
    },
    {
      question: 'How do I get started?',
      answer: 'Simply contact us through our Contact page or schedule a free consultation. We\'ll discuss your requirements, challenges, and goals, then propose a customized solution that fits your needs and budget.'
    },
    {
      question: 'What industries do you serve?',
      answer: 'We serve a wide range of industries including healthcare, senior living, technology startups, mid-market companies, and enterprises. Our solutions are customizable to meet the specific needs of any industry requiring robust infrastructure and cloud services.'
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="FAQ — Frequently Asked Questions | NIVA Digital AI"
        description="Find answers to common questions about NIVA Digital's infrastructure teams, GCC consulting, and cloud managed services."
        keywords="teams FAQ, GCC consulting questions, cloud services FAQ, development questions"
        canonical={`${process.env.REACT_APP_BASE_URL || 'https://nivadigital.io'}/faq`}
      />
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center">
            <div className="inline-flex items-center space-x-2 bg-red-100 text-red-600 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <HelpCircle size={20} />
              <span>Frequently Asked Questions</span>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight">
              How Can We
              <span className="block text-red-600 mt-2">Help You?</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Find answers to common questions about our services, processes, and partnerships
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <Card className="border-2">
            <CardContent className="pt-8">
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger className="text-left text-lg font-semibold hover:text-red-600">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-600 leading-relaxed">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>

          {/* Still Have Questions */}
          <div className="mt-12 text-center">
            <Card className="border-2 border-red-600 bg-red-50">
              <CardContent className="pt-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Still Have Questions?</h3>
                <p className="text-gray-600 mb-6">
                  Can't find the answer you're looking for? Our team is here to help.
                </p>
                <a href="/contact">
                  <button className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors">
                    Contact Us
                  </button>
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;